// routes/_authenticated/documents/$type/$cnpj/$year/$referenceDate/$idDoc/$versao/index.tsx
// — document detail route WITH version. Same shape as the no-version sibling
// (`$idDoc/index.tsx`), but reads one extra path segment and forwards it to
// the API client so the backend can pin the right `Versao` for restated
// filings.
//
// ponytail: `versao` is read only from the `$versao` slot — `parseRouteParams`
// is invoked with `withVersao: true` so the helper attaches `versao` to the
// resulting `DocumentParams`. The sibling index.tsx (no version) passes
// `{ withVersao: false }` (the default) so `/documents/ipe/.../foo` can't
// silently inject a versao through key confusion.
import { createFileRoute, Link } from "@tanstack/react-router";

import { PageShell } from "@/components/page-shell";
import { Button } from "@/components/ui/button";
import {
  CgvnDocumentPage,
  DfpDocumentPage,
  FcaDocumentPage,
  FreDocumentPage,
  IpeDocumentPage,
  ItrDocumentPage,
  UnsupportedDocumentPage,
  VlmoDocumentPage,
} from "@/features/documents";
import type { DocumentKey, DocumentType } from "@/features/documents/api";
import { EMPTY_PARAMS, parseRouteParams } from "@/features/documents/route-params";
import { BackButton } from "@/routes/_authenticated/documents/$type/-back-button";

const SUPPORTED_TYPES: DocumentType[] = ["FRE", "ITR", "DFP", "CGVN", "FCA", "VLMO", "IPE"];

function normalizeType(raw: string): DocumentType {
  const t = raw.toUpperCase() as DocumentType;
  return SUPPORTED_TYPES.includes(t) ? t : "FRE";
}

export const Route = createFileRoute(
  "/_authenticated/documents/$type/$cnpj/$year/$referenceDate/$idDoc/$versao/",
)({
  component: DocumentDetailRoute,
});

function DocumentDetailPage({ type, docKey }: { type: DocumentType; docKey: DocumentKey }) {
  if (type === "ITR") return <ItrDocumentPage docKey={docKey} />;
  if (type === "DFP") return <DfpDocumentPage docKey={docKey} />;
  if (type === "CGVN") return <CgvnDocumentPage docKey={docKey} />;
  if (type === "FCA") return <FcaDocumentPage docKey={docKey} />;
  if (type === "VLMO") return <VlmoDocumentPage docKey={docKey} />;
  if (type === "IPE") return <IpeDocumentPage docKey={docKey} />;
  return <FreDocumentPage docKey={docKey} />;
}

function DocumentDetailRoute() {
  const params = Route.useParams();
  const parsed = parseRouteParams(params, { withVersao: true });
  const ready = parsed !== EMPTY_PARAMS;
  const type = ready ? normalizeType(parsed.type) : null;

  return (
    <PageShell
      title={type ? `Documento ${type}` : "Documento"}
      description="Detalhe completo do formulário entregue à CVM."
      actions={ready ? <BackButton /> : null}
    >
      {!ready ? (
        <MissingSegmentCard />
      ) : type && SUPPORTED_TYPES.includes(type) ? (
        <DocumentDetailPage
          type={type}
          docKey={{
            year: parsed.year,
            cnpj: parsed.cnpj,
            referenceDate: parsed.referenceDate,
            idDoc: parsed.idDoc,
            version: parsed.versao,
          }}
        />
      ) : (
        <UnsupportedDocumentPage type={(type ?? "FRE") as DocumentType} />
      )}
    </PageShell>
  );
}

// ponytail: identical copy / affordances to the no-version sibling; users
// land here either from a valid sharing link or from a deep link broken by
// the dashboard restructure. Both pages route to the same explanation.
function MissingSegmentCard() {
  return (
    <div
      className="flex flex-col items-center gap-3 rounded-md border border-dashed py-12 text-center"
      data-testid="document-missing-segment"
    >
      <div className="text-sm font-medium">URL de documento incompleta</div>
      <div className="text-muted-foreground max-w-md text-xs">
        Esta rota exige <code>type</code>, <code>cnpj</code>, <code>year</code>,{" "}
        <code>referenceDate</code>, <code>idDoc</code> e (opcional) <code>versao</code> como
        segmentos do caminho. Volte ao dashboard e clique em um filing recente.
      </div>
      <Button asChild variant="outline" size="sm">
        <Link to="/dashboard">Voltar ao dashboard</Link>
      </Button>
    </div>
  );
}
